const STRENGTH_TYPES = ['STRENGTH', 'CLASSICSTRENGTH', 'SUPERSETS', 'QUALITY'];
const RUN_TYPES = ['RUN'];
const AMRAP_TYPES = ['AMRAP'];
const TIMED_TYPES = ['EMOM', 'INTERVAL'];

const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const formatExerciseStats = (record, blockType) => {
  if (!record) return '';

  if (STRENGTH_TYPES.includes(blockType)) {
    const parts = [];
    if (record.sets) parts.push(`${record.sets} sets`);
    if (record.reps) parts.push(`of ${record.reps} reps`);
    if (record.weight) {
      parts.push(`@ ${record.weight}kg`);
    }
    return parts.join(' ');
  }

  if (RUN_TYPES.includes(blockType)) {
    const parts = [];
    if (record.time) parts.push(formatTime(record.time));
    if (record.distance) parts.push(`${record.distance} km`);
    return parts.join(' · ');
  }

  if (AMRAP_TYPES.includes(blockType)) {
    const rounds = record.quantity;
    if (rounds && record.time) return `${rounds} rounds in ${formatTime(record.time)}`;
    if (rounds) return `${rounds} rounds`;
    return '';
  }

  if (TIMED_TYPES.includes(blockType)) {
    if (record.quantity && record.unit) return `${record.quantity} ${record.unit.toLowerCase()}`;
    return '';
  }

  // Fallback for CUSTOM, PILATESR and others
  const parts = [];
  if (record.sets) parts.push(`${record.sets} sets`);
  if (record.reps) parts.push(`of ${record.reps} reps`);
  if (record.quantity && record.unit) parts.push(`${record.quantity} ${record.unit.toLowerCase()}`);
  return parts.join(' ');
};

export const getBlockTypeLabel = (blockType) => {
  const labels = {
    STRENGTH: 'Strength',
    CLASSICSTRENGTH: 'Classic Strength',
    SUPERSETS: 'Super Sets',
    QUALITY: 'Quality',
    RUN: 'Run',
    AMRAP: 'AMRAP',
    EMOM: 'EMOM',
    INTERVAL: 'Interval',
    CUSTOM: 'Custom',
  };
  return labels[blockType] || blockType;
};

export const getStatBadges = (record, blockType) => {
  if (!record) return [];

  if (STRENGTH_TYPES.includes(blockType)) {
    const badges = [];
    if (record.sets) badges.push({ label: 'Sets', value: record.sets });
    if (record.reps) badges.push({ label: 'Reps', value: record.reps });
    if (record.weight) badges.push({ label: 'Weight', value: `${record.weight}kg` });
    return badges;
  }

  if (RUN_TYPES.includes(blockType)) {
    const badges = [];
    if (record.time) badges.push({ label: 'Time', value: formatTime(record.time) });
    if (record.distance) badges.push({ label: 'Distance', value: `${record.distance} km` });
    return badges;
  }

  if (AMRAP_TYPES.includes(blockType)) {
    const badges = [];
    if (record.quantity) badges.push({ label: 'Rounds', value: record.quantity });
    if (record.time) badges.push({ label: 'Time', value: formatTime(record.time) });
    return badges;
  }

  if (TIMED_TYPES.includes(blockType)) {
    if (record.quantity && record.unit)
      return [{ label: record.unit, value: record.quantity }];
    return [];
  }

  // Fallback for CUSTOM, PILATESR and others
  const badges = [];
  if (record.sets) badges.push({ label: 'Sets', value: record.sets });
  const repsValue = record.quantity || record.reps;
  if (repsValue) badges.push({ label: 'Reps', value: repsValue });
  return badges;
};

export const isStrengthType = (blockType) => STRENGTH_TYPES.includes(blockType);
export const isRunType = (blockType) => RUN_TYPES.includes(blockType);

export const getChartInfo = (entries, blockType, mode = 'weight') => {
  if (!entries || entries.length < 2) return null;

  const sorted = [...entries].reverse(); // oldest first
  const limited = sorted.slice(-7);

  const labels = limited.map((e) => {
    const d = new Date(e.date);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  });

  if (STRENGTH_TYPES.includes(blockType)) {
    if (mode === 'volume') {
      const data = limited.map((e) => {
        const w = e.record?.weight || 0;
        const s = e.record?.sets || 0;
        const r = parseInt(e.record?.reps, 10) || 0;
        return w * s * r;
      });
      return { labels, data, suffix: '', title: 'Volume Progression (kg)' };
    }
    const data = limited.map((e) => e.record?.weight || 0);
    return { labels, data, suffix: 'kg', title: 'Weight Progression' };
  }

  if (RUN_TYPES.includes(blockType)) {
    if (mode === 'pace') {
      const data = limited.map((e) => {
        const totalSec = e.record?.time || 0;
        const dist = e.record?.distance || 0;
        if (dist > 0 && totalSec > 0) {
          return Math.round((dist / (totalSec / 3600)) * 100) / 100;
        }
        return 0;
      });
      return { labels, data, suffix: '', title: 'Pace (km/h)' };
    }
    const data = limited.map((e) => e.record?.distance || 0);
    return { labels, data, suffix: 'km', title: 'Distance Progression' };
  }

  if (AMRAP_TYPES.includes(blockType)) {
    const data = limited.map((e) => e.record?.quantity || 0);
    return { labels, data, suffix: '', title: 'Rounds Progression' };
  }

  // Fallback
  const data = limited.map((e) => e.record?.quantity || 0);
  const unit = limited[0]?.record?.unit || '';
  return { labels, data, suffix: unit ? unit.toLowerCase() : '', title: 'Progress' };
};

export const getEditableFields = (blockType) => {
  if (STRENGTH_TYPES.includes(blockType)) {
    return [
      { key: 'sets', label: 'Sets', type: 'number' },
      { key: 'reps', label: 'Reps', type: 'text' },
      { key: 'weight', label: 'Weight (kg)', type: 'number', step: '0.5' },
    ];
  }
  if (RUN_TYPES.includes(blockType)) {
    return [
      { key: 'time', label: 'Time (seconds)', type: 'number' },
      { key: 'distance', label: 'Distance (km)', type: 'number', step: '0.1' },
    ];
  }
  if (AMRAP_TYPES.includes(blockType)) {
    return [
      { key: 'quantity', label: 'Rounds', type: 'number' },
      { key: 'time', label: 'Time (seconds)', type: 'number' },
    ];
  }
  if (TIMED_TYPES.includes(blockType)) {
    return [
      { key: 'quantity', label: 'Quantity', type: 'number' },
      { key: 'unit', label: 'Unit', type: 'text' },
    ];
  }
  return [
    { key: 'sets', label: 'Sets', type: 'number' },
    { key: 'reps', label: 'Reps', type: 'text' },
    { key: 'weight', label: 'Weight', type: 'number', step: '0.5' },
    { key: 'quantity', label: 'Quantity', type: 'number' },
  ];
};
